import { Box, Button, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import Breadcrumb from '@/components/shared/Breadcrumb';
import { useAddOrganization } from '@/hooks/useAddOrganization';
import { useSnackbar } from '@/hooks/useSnackbar';
import { ROUTES } from '@/routes';
import { handleSnackbarError } from '@/shared/utils/errorHandler';
import { useTheme } from '@/theme/useTheme';

import { FormErrors, FormValues, OrganizationFormFields } from '../components/OrganizationFormFields';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div role='tabpanel' hidden={value !== index} id={`vertical-tabpanel-${index}`} aria-labelledby={`vertical-tab-${index}`} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function allProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
}

function AddOrganization() {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const navigate = useNavigate();

  const { addOrganization, isPending, validationErrors } = useAddOrganization();
  const snackbar = useSnackbar();

  const [values, setValues] = useState<FormValues>({
    displayName: '',
    organizationCode: '',
    organizationClaim: '',
    logoUrl: '',
    isActive: false,
  });

  const getTab = (params: URLSearchParams) => {
    const tab = parseInt(params.get('tab') || '0', 10);
    return isNaN(tab) || tab < 0 || tab > 1 ? 0 : tab;
  };

  const currentTab = getTab(searchParams);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const params = new URLSearchParams(searchParams);
    if (newValue === 0) params.delete('tab');
    else params.set('tab', newValue.toString());
    setSearchParams(params, { replace: true });
  };

  const location = useLocation();
  const breadcrumbPath = useMemo(() => {
    const state = location.state as { returnSearchParams?: string } | undefined;
    const returnSearchParams = state?.returnSearchParams;
    if (returnSearchParams) {
      return `${ROUTES.ADMIN_ORGANIZATION}?${returnSearchParams}`;
    }
    return ROUTES.ADMIN_ORGANIZATION;
  }, [location.state]);

  const breadcrumbItems = [
    { label: 'Manage Clients for Account Admin', path: breadcrumbPath },
    { label: 'Add Client', path: ROUTES.ADMIN_ORGANIZATION_ADD },
  ];

  const handleFieldChange = (field: keyof Omit<FormValues, 'isActive'>) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleFieldClear = (field: keyof Omit<FormValues, 'isActive'>) => {
    setValues((prev) => ({ ...prev, [field]: '' }));
  };

  const handleCancel = () => {
    navigate(breadcrumbPath);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const result = await addOrganization({
        name: values.displayName,
        organizationCode: values.organizationCode,
        organizationClaim: values.organizationClaim,
        logoUrl: values.logoUrl,
        status: values.isActive ? 'Active' : 'Inactive',
      });

      snackbar.showSuccess('Client created successfully');

      if (result.id) {
        navigate(ROUTES.ADMIN_ORGANIZATION);
      }
    } catch (err) {
      handleSnackbarError(err, snackbar);
    }
  };

  const fieldErrors: FormErrors = {
    displayName: validationErrors?.['Name']?.[0],
    organizationCode: validationErrors?.['OrganizationCode']?.[0],
    organizationClaim: validationErrors?.['OrganizationClaim']?.[0],
    logoUrl: validationErrors?.['LogoUrl']?.[0],
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Breadcrumb items={breadcrumbItems} />
      <Typography
        data-testid='add-organization-title'
        sx={{
          fontSize: theme.hhglobal.typography.headings.h1.fontSize,
          lineHeight: theme.hhglobal.typography.headings.h1.lineHeight,
          marginBottom: theme.hhglobal.spacing.spacing[2].valuePx,
          color: theme.hhglobal.neutral.superblack,
        }}
      >
        New Client
      </Typography>
      <Box sx={{ display: 'flex', gap: theme.hhglobal.spacing.spacing[7].valuePx, width: '100%' }}>
        <Tabs
          orientation='vertical'
          variant='scrollable'
          value={currentTab}
          onChange={handleTabChange}
          aria-label='Add organization tabs'
          data-testid='add-organization-tabs'
          sx={{
            minWidth: 200,
            '& .MuiTabs-indicator': {
              left: 0,
              right: 'auto',
              backgroundColor: '#000000',
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              textAlign: 'left',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              color: '#000000',
              minHeight: '40px',
              padding: '12px',
              fontSize: '14px',
              fontWeight: 400,
            },
            '& .MuiTab-root.Mui-selected': {
              fontWeight: 700,
              color: '#000000',
            },
          }}
        >
          <Tab label='Client Information' {...allProps(0)} data-testid='tab-organization-information' />
          <Tab label='Account Admin Access' {...allProps(1)} disabled data-testid='tab-account-admin-access' />
          <Tab label='System Access' {...allProps(2)} disabled data-testid='tab-system-admin-access' />
        </Tabs>
        <Box sx={{ flexGrow: 1 }}>
          <TabPanel value={currentTab} index={0}>
            <Box
              component='form'
              noValidate
              autoComplete='off'
              onSubmit={handleSubmit}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.hhglobal.spacing.spacing[7].valuePx,
              }}
            >
              <OrganizationFormFields
                values={values}
                fieldErrors={fieldErrors}
                onChange={handleFieldChange}
                onClear={handleFieldClear}
                onToggleStatus={(checked) => setValues((prev) => ({ ...prev, isActive: checked }))}
                idPrefix='add-org'
              />

              <Stack direction='row' spacing={theme.hhglobal.spacing.spacing[4].valuePx}>
                <Button
                  variant='outlined'
                  data-testid='cancel-button'
                  onClick={handleCancel}
                  sx={{
                    height: '36px',
                    fontSize: '14px',
                    boxShadow: 'none',
                    textTransform: 'none',
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type='submit'
                  variant='contained'
                  disabled={isPending}
                  data-testid='save-client-button'
                  sx={{
                    height: '36px',
                    fontSize: '14px',
                    boxShadow: 'none',
                    textTransform: 'none',
                    fontWeight: theme.hhglobal.typography.fontWeights.regular,
                    '&:active': {
                      backgroundColor: theme.hhglobal.neutral.gray900,
                      color: theme.palette.common.white,
                    },
                  }}
                >
                  {isPending ? 'Saving...' : 'Save Client'}
                </Button>
              </Stack>
            </Box>
          </TabPanel>
          <TabPanel value={currentTab} index={1} />
        </Box>
      </Box>
    </Box>
  );
}

export default AddOrganization;
